import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import styled, { css } from 'styled-components';
import { Button } from '../../form/Button';
import FormTitle from '../../form/FormTitle';
import CloseIcon from '../../../assets/icons/CloseIcon';
import StyledText from '../../StyledComponents/StyledText';

import { DEFAULT_BASIC_CONFIG, FieldConfig, Field, FieldType, Scope, FieldDataTypeEnum } from '../../../types/types';
import { FieldLabelSettings } from './FieldLabelSettings';
import { PlaceholderSettings } from './PlaceholderSettings';
import { CharacterLimitSettings } from './CharacterLimitSettings';
import { RangeSettings } from './RangeSettings';
import { ThousandSeparatorSettings } from './ThousandSeperatorSettings';
import ChoiceListSettings from './ChoiceListSettings';
import TimeFieldSettings from './TimeFieldSettings';
import DateFieldSettings from './DateSettings';
import MandatoryFieldSettings from './MandatoryFieldSettings';
import { AddressSettings } from './AddressSettings';

import {
  getIconForDataType,
  showAudioVideoEmbedSettings,
  showBooleanSettings,
  showCharacterLimitSettings,
  showChoiceListSettings,
  showCurrencySelectorSettings,
  // showDateFieldSettings,
  showDescriptionSettings,
  showFileUploadSettings,
  showImageFieldSettings,
  showMandatoryFieldSettings,
  showMonthYearFieldSettings,
  showPlaceholderSettings,
  showRangeSettings,
  showSliderFieldSettings,
  showThousandSeparatorSettings,
  showTimeFieldSettings,
  showVisibilitySettings,
} from './fieldSettingsHelper';
import { DecimalPlacesSettings } from './DecimalPlacesSettings';
import { VisibilitySettings } from './VisibilitySettings';
import { CurrencySelectorSettings } from './CurrencySelectorSettings';
import MonthYearFieldSettings from './MonthYearFieldSettings';
import FileUploadSettings from './FileUploadSettings';
import ImageUploadSettings from './ImageUploadSettings';
import SliderSettings from './SliderSettings';
import DescriptionSettings from './DescriptionSettings';
import AudioVideoEmbedSettings from './AudioVideoEmbedSettings';
import { FormActions, FormContainer, ModalContent, ModalOverlay } from '../CustomForm/FormFiller';
import AMSSelect from '../../AMSSelect';
import CustomInput from '../../form/CustomInput';
import { useGetAllDataTypesQuery, useGetAllFieldsQuery, useGetScopesforFieldsQuery, useLazyGetAccessKeyAvailabilityQuery } from '../../../api/formsApiSlice';
import { useTheme } from '../../../middleware/ThemeContext';
import CustomSelect from '../../form/CustomSelect';
import { CheckboxItem } from '../../listing/ColumnIdentifier';
import { set } from 'lodash';
import { isVisible } from '@testing-library/user-event/dist/utils';
import { data } from 'react-router-dom';
import CustomTextArea from '../../form/CustomTextArea';
import { access } from 'fs';
import SaveIcon from '../../../assets/icons/SaveIcon';

interface FieldSettingsModalProps {
  show: boolean;
  onHide: () => void;
  fieldId?: string | number;
  config: FieldConfig;
  fieldType?: FieldType;
  fieldDataType?: FieldDataTypeEnum;
  fieldAccessKey?: string | null;
  fieldName?: string;
  fieldScope?: string;
  onSave?: (config: FieldConfig) => void;
  isCreating?: boolean;
  // NOTE: accessKey is provided by the UI and will be included in payload as `accessKey`.
  onSaveForCreate?: (name: string, fieldDataType: FieldDataTypeEnum, fieldScope: string, config: FieldConfig, accessKey: string) => void;
  onSaveForUpdate?: (name: string, fieldDataType: FieldDataTypeEnum, fieldScope: string, config: FieldConfig, accessKey: string) => void;

  readOnly?: boolean;
}

export const FieldSettingsModal: React.FC<FieldSettingsModalProps> = ({
  show,
  onHide,
  fieldId,
  config,
  fieldType,
  fieldDataType,
  fieldAccessKey,
  fieldName,
  fieldScope,
  onSave,
  isCreating,
  onSaveForCreate,
  onSaveForUpdate,
  readOnly = false,
}) => {
  const { theme } = useTheme();
  const [localConfig, setLocalConfig] = useState<FieldConfig>(config);
  const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
  const [localFieldAccessKey, setLocalFieldAccessKey] = useState<string | null>(fieldAccessKey || null);
  const [localAccessKeySuffix, setLocalAccessKeySuffix] = useState<string>('');
  const [fieldAccessKeyError, setFieldAccessKeyError] = useState<string | null>(null);
  const [isCheckingAccessKey, setIsCheckingAccessKey] = useState(false);
  const [isAccessKeyAvailable, setIsAccessKeyAvailable] = useState<boolean | null>(null);
  // suggestions removed per UX: auto-generate key during creation and lock during update
  const [localFieldDataType, setLocalFieldDataType] = useState<string | undefined>(fieldDataType || undefined);
  const [localFieldName, setLocalFieldName] = useState<string>(fieldName || '');
  const [localFieldScope, setLocalFieldScope] = useState<string>(fieldScope || '');
  // const [localIsGlobalScopeField, setLocalIsGlobalScopeField] = useState<boolean>(isGlobalScopeField || false);
  const [fieldTypeError, setFieldTypeError] = useState<string | null>(null);
  const [fieldNameError, setFieldNameError] = useState<string | null>(null);
  const [fieldScopeError, setFieldScopeError] = useState<string | null>(null);
  const [fieldConfigs, setFieldConfigs] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [fieldsPage, setFieldsPage] = useState(1);
  const [fields, setFields] = useState<Field[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [limit] = useState(20);
  const scopesAdditionalParams = useMemo(() => ({ isVisible: true }), []);
  const [allScopes, setAllScopes] = useState<{ label: string; value: string; }[]>([]);
  const { data: allDataTypes } = useGetAllDataTypesQuery({});
  const dataTypes = allDataTypes?.data?.dataTypes || {};
  const dataTypesOptions = useMemo(() => (
    Object.keys(dataTypes)
      .filter((key) => dataTypes[key] !== 'DECIMAL')
      .map((key) => {
        const value = dataTypes[key];
        const isCheckbox = value === 'CHECKBOX';

        return {
          label: isCheckbox ? 'Boolean' : key.replace(/_/g, ' '),
          value,
        };
      })
  ), [dataTypes]);
  const dataTypeValueToLabel = useMemo(() => {
    return dataTypesOptions.reduce<Record<string, string>>((acc, option) => {
      if (option.value) {
        acc[String(option.value)] = option.label;
      }
      return acc;
    }, {});
  }, [dataTypesOptions]);

  const selectedDataTypeOption = useMemo(() => {
    if (!localFieldDataType) return null;
    const label = dataTypeValueToLabel[localFieldDataType] ?? localFieldDataType.replace(/_/g, ' ');
    return {
      value: localFieldDataType,
      label,
    };
  }, [dataTypeValueToLabel, localFieldDataType]);

  const mapDataTypesResponse = useCallback((json: any, page: number) => {
    const responseData = json?.data?.dataTypes ?? json?.dataTypes ?? {};
    const options = Object.keys(responseData)
      .filter((key) => responseData[key] !== 'DECIMAL')
      .map((key) => {
        const value = responseData[key];
        const isCheckbox = value === 'CHECKBOX';
        return {
          label: isCheckbox ? 'Boolean' : key.replace(/_/g, ' '),
          value,
          icon: getIconForDataType(value),
        };
      });

    return {
      options,
      hasMore: false,
      additional: { page },
    };
  }, []);

  const mapScopesResponse = useCallback((json: any, page: number) => {
    const scopesList = json?.scopes ?? [];
    const pagination = json?.pagination ?? {};
    const perPage = pagination?.perPage ?? limit;
    const totalCount = pagination?.total ?? json?.totalCount ?? scopesList.length;
    const hasMore = totalCount ? page * perPage < totalCount : scopesList.length === perPage;

    const options = scopesList.map((scope: Scope) => ({
      label: scope?.displayName,
      value: String(scope?.id ?? ''),
      ...scope,
    }));

    return {
      options,
      hasMore,
      additional: { page: page + 1 },
    };
  }, [limit]);

  const { data: scopes } = useGetScopesforFieldsQuery({
    isVisible: true,
    page: 1,
    limit
  });
  // Helper functions
  const updateConfig = (updates: Partial<FieldConfig['uiMeta']>) => {
    setLocalConfig(prev => ({
      ...prev,
      uiMeta: {
        ...prev.uiMeta,
        ...updates
      }
    }));
  };

  const updateOptions = (options: string[]) => {
    setLocalConfig(prev => ({
      ...prev,
      options
    }));
  };

  const updateFieldProperty = (key: string, property: string, value: any) => {
    setFieldConfigs(prev =>
      prev.map(field => field.key === key ? { ...field, [property]: value } : field)
    );
  };

  const handleDataTypeSelect = (option: any) => {
    const selectedOption = Array.isArray(option) ? option[0] : option;
    const nextValue = selectedOption?.value as string | undefined;
    setLocalFieldDataType(nextValue);
    if (nextValue) {
      setFieldTypeError(null);
      setLocalConfig(DEFAULT_BASIC_CONFIG);
    }
  };

  const handleScopeSelect = (option: any) => {
    const selectedOption = Array.isArray(option) ? option[0] : option;
    const nextValue = selectedOption?.value ? String(selectedOption.value) : '';
    setLocalFieldScope(nextValue);
    if (nextValue) {
      setFieldScopeError(null);
      if (selectedOption?.label) {
        setAllScopes((prev) => {
          const exists = prev.some((scope) => String(scope.value) === nextValue);
          if (exists) return prev;
          return [...prev, { label: selectedOption.label, value: nextValue }];
        });
      }
    }
  };

  useEffect(() => {
    if (!scopes?.scopes) return;
    const newOptions = scopes.scopes.map((scope: Scope) => ({
      label: scope?.displayName,
      value: String(scope?.id ?? ''),
    }));
    setAllScopes(newOptions);
  }, [scopes]);

  // Keep local scope in sync with prop when modal opens
  useEffect(() => {
    if (show) {
      setLocalFieldScope(fieldScope || '');
    }
  }, [fieldScope, show]);

  // Resolve a friendly label for the current scope
  const selectedScopeLabel = useMemo(() => {
    const id = localFieldScope;
    if (!id) return 'N/A';
    const opt = allScopes.find(o => String(o.value) === String(id));
    if (opt) return opt.label;
    const fallback = scopes?.scopes?.find((s: Scope) => String(s.id) === String(id));
    return fallback?.displayName || `Scope ${id}`;
  }, [localFieldScope, allScopes, scopes]);

  const selectedScopeOption = useMemo(() => {
    if (!localFieldScope) return null;
    const label = selectedScopeLabel && selectedScopeLabel !== 'N/A'
      ? selectedScopeLabel
      : `Scope ${localFieldScope}`;
    return {
      value: localFieldScope,
      label,
    };
  }, [localFieldScope, selectedScopeLabel]);

  // Compute prefix for access key
  const prefix = useMemo(() => {
    if (!localFieldScope) return '';
    const scopeBase = (selectedScopeLabel || '').trim().toLowerCase();
    const scopeSlug = scopeBase.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/__+/g, '_');
    return scopeSlug && scopeSlug !== 'n_a' ? `${scopeSlug}_` : '';
  }, [localFieldScope, selectedScopeLabel]);

  // Compute full access key
  const fullAccessKey = useMemo(() => prefix + localAccessKeySuffix, [prefix, localAccessKeySuffix]);

  // Update localFieldAccessKey when fullAccessKey changes
  useEffect(() => {
    setLocalFieldAccessKey(fullAccessKey || null);
  }, [fullAccessKey]);

  // Auto-generate access key from name when creating and check availability (debounced)
  useEffect(() => {
    if (!isCreating) return; // only auto-generate while creating

    const base = (localFieldName || '').trim().toLowerCase();
    const slug = base.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/__+/g, '_');
    setLocalAccessKeySuffix(slug);

    setFieldAccessKeyError(null);
    setIsAccessKeyAvailable(null);
  }, [localFieldName, isCreating]);

  // use the api slice lazy query to check access key availability
  const [triggerCheckAccessKey] = useLazyGetAccessKeyAvailabilityQuery();

  const checkAccessKeyAvailability = async (accessKey: string) => {
    const key = (accessKey || '').trim();
    if (!key) {
      setFieldAccessKeyError('Field access key is required');
      setIsAccessKeyAvailable(false);
      return false;
    }
    try {
      setIsCheckingAccessKey(true);
      setFieldAccessKeyError(null);
      // trigger the RTK Query lazy endpoint and unwrap the response
      // pass fieldId so backend can exclude current field when checking availability
      const json = await triggerCheckAccessKey({ accessKey: key, fieldId: fieldId ?? (config as any)?.id ?? undefined }).unwrap();
      const fieldExist = Boolean(json?.fieldExist);
      const available = !fieldExist;
      setIsAccessKeyAvailable(available);
      if (!available) setFieldAccessKeyError('Access key is already taken');
      else setFieldAccessKeyError(null);
      return available;
    } catch (err) {
      setFieldAccessKeyError('Could not validate access key');
      setIsAccessKeyAvailable(false);
      return false;
    } finally {
      setIsCheckingAccessKey(false);
    }
  };

  // const handleLoadMoreFields = () => {
  //   if (!isFetchingFields && fieldsData?.totalCount > allStandardFields.length) {
  //     setFieldsPage((prev) => prev + 1);
  //   }
  // };
  useEffect(() => {
    if (isCreating) {
      setLocalConfig(DEFAULT_BASIC_CONFIG);
    } else if (fieldDataType) {
      setLocalFieldDataType(fieldDataType);
    }
  }, [isCreating, fieldType]);

  useEffect(() => {
    if (!show) return;
    setLocalConfig(config);
    // ensure we have the current field id available for access-key checks
    setSelectedFieldId((config as any)?.id ?? null);
  }, [config, show]);

  // Extract suffix from existing access key in edit mode
  useEffect(() => {
    if (isCreating || !fieldAccessKey) return;
    const suffix = fieldAccessKey.startsWith(prefix) ? fieldAccessKey.slice(prefix.length) : fieldAccessKey;
    setLocalAccessKeySuffix(suffix);
  }, [isCreating, fieldAccessKey, prefix]);

  const validateForm = () => {
    let isValid = true;

    // Validate field type
    if (isCreating && !localFieldDataType) {
      setFieldTypeError('Field type is required');
      isValid = false;
    } else {
      setFieldTypeError(null);
    }

    // Validate field name
    if (!localFieldName.trim()) {
      setFieldNameError('Field name is required');
      isValid = false;
    } else {
      setFieldNameError(null);
    }

    // Validate scopes (single-scope model requires at least one)
    if (!localFieldScope) {
      setFieldScopeError('At least one scope must be selected');
      isValid = false;
    } else {
      setFieldScopeError(null);
    }

    // Validate access key
    if (!localAccessKeySuffix || !localAccessKeySuffix.trim()) {
      setFieldAccessKeyError('Field access key suffix is required');
      isValid = false;
    } else {
      // clear previous error but keep availability state
      setFieldAccessKeyError(null);
    }

    return isValid;
  };

  const [choiceErrors, setChoiceErrors] = useState<number[]>([]);

  const validateChoices = () => {
    const emptyIndexes = (localConfig.options || [])
      .map((choice, i) => (choice.trim() === '' ? i : -1))
      .filter((i) => i !== -1);

    setChoiceErrors(emptyIndexes);
    return emptyIndexes.length === 0;
  };


  const handleSave = async () => {
    if (!validateForm()) return;
    const isChoicesValid = showChoiceListSettings(localFieldDataType) ? validateChoices() : true;
    if (!isChoicesValid) return;
    setIsSaving(true);
    try {
      // ensure access key availability if not already confirmed
      if (localFieldAccessKey) {
        const available = await checkAccessKeyAvailability(localFieldAccessKey);
        if (!available) {
          setIsSaving(false);
          return;
        }
      }
      if (isCreating && localFieldDataType && onSaveForCreate) {
        await onSaveForCreate(
          localFieldName,
          localFieldDataType as FieldDataTypeEnum,
          localFieldScope,
          localConfig,
          (localFieldAccessKey || '').trim()
        );
      } else if (!isCreating && onSaveForUpdate && localFieldDataType) {
        await onSaveForUpdate(
          localFieldName,
          localFieldDataType as FieldDataTypeEnum,
          localFieldScope,
          localConfig,
          (localFieldAccessKey || '').trim()
        );
      }
      onClose();
    } finally {
      setIsSaving(false);

    }
  };

  const renderableSections = [
    showBooleanSettings(localFieldDataType) && (
      <FieldLabelSettings
        key="label"
        fieldLabel={localConfig.uiMeta.fieldLabel || ''}
        setFieldLabel={(value) => updateConfig({ fieldLabel: value })}
        hideLabel={localConfig.uiMeta.hideLabel || false}
        setHideLabel={(value) => updateConfig({ hideLabel: value })}
      />
    ),
    // Always-available Display Name input for form label (styled like Field Name)
    true && (
      <div key="display-name" className="px-3 py-2">
        <FieldLabel theme={theme}>
          Display Name (shown on form)
        </FieldLabel>
        <CustomInput
          value={localConfig.uiMeta.displayName || ''}
          onChange={(val) => updateConfig({ displayName: val })}
          placeholder="Enter display name shown on form"
          width="100%"
        />
      </div>
    ),
    showPlaceholderSettings(localFieldDataType) && (
      <PlaceholderSettings
        key="placeholder"
        isCompoundField={false}
        placeholder={localConfig.uiMeta.placeholder || ''}
        setPlaceholder={(value) => updateConfig({ placeholder: value })}
        fieldConfigs={fieldConfigs}
        updateFieldProperty={updateFieldProperty}
      />
    ),
    // showCharacterLimitSettings(localFieldDataType) && (
    //   <CharacterLimitSettings
    //     key="charLimit"
    //     characterLimitEnabled={localConfig.uiMeta.characterLimitEnabled || false}
    //     setCharacterLimitEnabled={(value) => updateConfig({ characterLimitEnabled: value })}
    //     maxLength={localConfig.uiMeta.maxLength?.toString() || ''}
    //     setMaxLength={(value) => updateConfig({ maxLength: value ? parseInt(value) : undefined })}
    //     minEnabled={localConfig.uiMeta.minEnabled || false}
    //     setMinEnabled={(value) => updateConfig({ minEnabled: value })}
    //     minValue={localConfig.uiMeta.minValue?.toString() || ''}
    //     setMinValue={(value) => updateConfig({ minValue: value ? parseInt(value) : undefined })}
    //   />
    // ),
    // showRangeSettings(localFieldDataType) && (
    //   <RangeSettings
    //     key="range"
    //     rangeEnabled={localConfig.uiMeta.rangeEnabled || false}
    //     setRangeEnabled={(value) => updateConfig({ rangeEnabled: value })}
    //     minValue={localConfig.uiMeta.minRange?.toString() || ''}
    //     setMinValue={(value) => updateConfig({ minRange: value })}
    //     maxValue={localConfig.uiMeta.maxRange?.toString() || ''}
    //     setMaxValue={(value) => updateConfig({ maxRange: value })}
    //   />
    // ),
    // showThousandSeparatorSettings(localFieldDataType) && (
    //   <ThousandSeparatorSettings
    //     key="separator"
    //     separatorOption={localConfig.uiMeta.separatorOption || 'none'}
    //     setSeparatorOption={(value) => updateConfig({ separatorOption: value })}
    //   />
    // ),
    // Numbers: Allow Decimal toggle + decimal places when enabled
    (localFieldDataType === 'NUMBERS') && (
      <div
        key="allow-decimal"
        className="px-3 py-2 d-flex align-items-center justify-content-between"
      >
        <FieldLabel style={{ margin: 0 }}>Allow Decimal</FieldLabel>
        <div className="form-check form-switch m-0">
          <input
            className="form-check-input"
            type="checkbox"
            checked={localConfig.uiMeta.allowDecimal || false}
            onChange={(e) => updateConfig({ allowDecimal: e.target.checked })}
          />
        </div>
      </div>
    ),
    (localFieldDataType === 'NUMBERS' && (localConfig.uiMeta.allowDecimal || false)) && (
      <DecimalPlacesSettings
        key="decimal-places"
        decimalPlaces={localConfig.uiMeta.decimalPlaces}
        setDecimalPlaces={(value) => updateConfig({ decimalPlaces: value })}
      />
    ),
    // showCurrencySelectorSettings(localFieldDataType) && (
    //   <CurrencySelectorSettings
    //     key="currency"
    //     selectedCurrency={localConfig.uiMeta.currency || 'USD'}
    //     setSelectedCurrency={(value) => updateConfig({ currency: value })}
    //     selectedDisplay={localConfig.uiMeta.currencyDisplay === "abbreviation" ? "abbreviation" : "symbol"}
    //     setSelectedDisplay={(value) => updateConfig({ currencyDisplay: value })}
    //   />
    // ),
    showChoiceListSettings(localFieldDataType) && (
      <ChoiceListSettings
        dataType={localFieldDataType}
        key="choices"
        choiceErrors={choiceErrors}
        setChoiceErrors={setChoiceErrors}
        useGlobalList={localConfig.uiMeta.useGlobalList ?? false}
        setUseGlobalList={(value) => updateConfig({ useGlobalList: value })}
        options={localConfig.options || []}
        setManualChoices={updateOptions}
        choiceFieldType={localConfig.uiMeta.choicesFieldType || "dropdown"}
        setChoiceFieldType={(value) => updateConfig({ choicesFieldType: value })}
        layout={localConfig.uiMeta.layout || "1"}
        setLayout={(value) => updateConfig({ layout: value })}
        choiceSortOrder={localConfig.uiMeta.choiceSortOrder || 'none'}
        setChoiceSortOrder={(value) => updateConfig({ choiceSortOrder: value })}
        minChoices={localConfig.uiMeta.minChoices || 0}
        setMinChoices={(value) => updateConfig({ minChoices: value })}
        maxChoices={localConfig.uiMeta.maxChoices || 1}
        setMaxChoices={(value) => updateConfig({ maxChoices: value })}
        includeOtherOption={localConfig.uiMeta.includeOtherOption || false}
        setIncludeOtherOption={(value: boolean) => updateConfig({ includeOtherOption: value })}
        defaultSelection={localConfig.uiMeta.defaultSelection || ''}
        setDefaultSelection={(val?: string) => updateConfig({ defaultSelection: val || '' })}
      />
    ),
    // showTimeFieldSettings(localFieldDataType) && (
    //   <TimeFieldSettings
    //     key="time"
    //     timeFormat={localConfig.uiMeta.timeFormat || "24"}
    //     setTimeFormat={(value) => updateConfig({ timeFormat: value })}
    //     minuteInterval={localConfig.uiMeta.minuteInterval || 15}
    //     setMinuteInterval={(value) => updateConfig({ minuteInterval: value })}
    //     initialTime={localConfig.uiMeta.initialTime && typeof localConfig.uiMeta.initialTime.ap === 'string'
    //       ? localConfig.uiMeta.initialTime
    //       : { hh: '', mm: '', ap: '' }}
    //     setInitialTime={(value) => updateConfig({ initialTime: { ...value, ap: value.ap ?? '' } })}
    //   />
    // ),
    // showDateFieldSettings(localFieldDataType) && (
    //   <DateFieldSettings
    //     key="date"
    //     initialValue={localConfig.uiMeta.initialValue}
    //     setInitialValue={(value) => updateConfig({ initialValue: value })}
    //     autoFill={localConfig.uiMeta.autoFill}
    //     setAutoFill={(value) => updateConfig({ autoFill: value })}
    //     weekStartDay={localConfig.uiMeta.weekStartDay ?? "sunday"}
    //     setWeekStartDay={(value) => updateConfig({ weekStartDay: value as "sunday" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" })}
    //     allowedDaysOfWeek={localConfig.uiMeta.allowedDaysOfWeek || 'All days of week'}
    //     setAllowedDaysOfWeek={(value) => updateConfig({ allowedDaysOfWeek: value })}
    //     allowedDates={localConfig.uiMeta.allowedDates}
    //     setAllowedDates={(value: { min?: string; max?: string }) => updateConfig({ allowedDates: value })}
    //   />
    // ),
    // showMonthYearFieldSettings(localFieldDataType) && (
    //   <MonthYearFieldSettings
    //     initialValue={localConfig.uiMeta.initialValue || ''}
    //     setInitialValue={(value) => updateConfig({ initialValue: value })}
    //     autoFill={localConfig.uiMeta.autoFill || false}
    //     setAutoFill={(value) => updateConfig({ autoFill: value })}
    //     displayMonth={localConfig.uiMeta.displayMonths || []}
    //     setDisplayMonth={(value) => updateConfig({ displayMonths: value })}
    //     allowedMonthYears={localConfig.uiMeta.allowedMonthYears || ''}
    //     setAllowedMonthYears={(value) => updateConfig({ allowedMonthYears: value })}
    //   />
    // ),
    // showFileUploadSettings(localFieldDataType) && (
    //   <FileUploadSettings
    //     allowedFormats={localConfig.uiMeta.allowedFormats || ''}
    //     setAllowedFormats={(value) => updateConfig({ allowedFormats: value })}
    //     uploadLimitMin={localConfig.uiMeta.uploadLimitMin?.toString() || ''}
    //     setUploadLimitMin={(value) => updateConfig({ uploadLimitMin: value ? parseInt(value) : undefined })}
    //     uploadLimitMax={localConfig.uiMeta.uploadLimitMax?.toString() || ''}
    //     setUploadLimitMax={(value) => updateConfig({ uploadLimitMax: value ? parseInt(value) : undefined })}
    //     fileSizeMin={localConfig.uiMeta.fileSizeMin?.toString() || ''}
    //     setFileSizeMin={(value) => updateConfig({ fileSizeMin: value ? parseFloat(value) : undefined })}
    //     fileSizeMinUnit={localConfig.uiMeta.fileSizeMinUnit || 'KB'}
    //     setFileSizeMinUnit={(value) => updateConfig({ fileSizeMinUnit: value as "KB" | "MB" | "GB" | undefined })}
    //     fileSizeMax={localConfig.uiMeta.fileSizeMax?.toString() || ''}
    //     setFileSizeMax={(value) => updateConfig({ fileSizeMax: value ? parseFloat(value) : undefined })}
    //     fileSizeMaxUnit={localConfig.uiMeta.fileSizeMaxUnit || 'MB'}
    //     setFileSizeMaxUnit={(value) => updateConfig({ fileSizeMaxUnit: value as "KB" | "MB" | "GB" | undefined })}
    //   />
    // ),
    // showImageFieldSettings(localFieldDataType) && (
    //   <ImageUploadSettings
    //     uploadLimitMin={localConfig.uiMeta.uploadLimitMinImage?.toString() || ''}
    //     setUploadLimitMin={(value) => updateConfig({ uploadLimitMinImage: value ? parseInt(value) : undefined })}
    //     uploadLimitMax={localConfig.uiMeta.uploadLimitMaxImage?.toString() || ''}
    //     setUploadLimitMax={(value) => updateConfig({ uploadLimitMaxImage: value ? parseInt(value) : undefined })}
    //     fileSizeMin={localConfig.uiMeta.fileSizeMinImage?.toString() || ''}
    //     setFileSizeMin={(value) => updateConfig({ fileSizeMinImage: value ? parseFloat(value) : undefined })}
    //     fileSizeMax={localConfig.uiMeta.fileSizeMaxImage?.toString() || ''}
    //     setFileSizeMax={(value) => updateConfig({ fileSizeMaxImage: value ? parseFloat(value) : undefined })}
    //     fileSizeMinUnit={localConfig.uiMeta.fileSizeMinUnitImage || 'MB'}
    //     setFileSizeMinUnit={(value) => updateConfig({ fileSizeMinUnit: value as "KB" | "MB" | "GB" | undefined })}
    //     fileSizeMaxUnit={localConfig.uiMeta.fileSizeMaxUnit || 'MB'}
    //     setFileSizeMaxUnit={(value) => updateConfig({ fileSizeMaxUnit: value as "KB" | "MB" | "GB" | undefined })}
    //     imageFormatType={localConfig.uiMeta.imageFormatTypeImage || ''}
    //     setImageFormatType={(value) => updateConfig({ imageFormatTypeImage: value })}
    //     cameraSelection={localConfig.uiMeta.cameraSelectionImage || ''}
    //     setCameraSelection={(value) => updateConfig({ cameraSelectionImage: value })}
    //     selectedUserInputs={localConfig.uiMeta.selectedUserInputsImage || []}
    //     setSelectedUserInputs={(value) => updateConfig({ selectedUserInputsImage: value })}
    //   />
    // ),
    true && (
      <> <div className="px-3 py-2">
        <FieldLabel theme={theme}>
          Tooltip Text (optional)
        </FieldLabel>
        <CustomTextArea
          value={localConfig.uiMeta.toolTipText || ''}
          onChange={(value) => updateConfig({ toolTipText: value })}
          placeholder="Enter text which will displayed against the checkbox" />
      </div> </>),
    showSliderFieldSettings(localFieldDataType) && (
      <SliderSettings
        key="slider"
        fieldSize={localConfig.uiMeta.fieldSize || 'Medium'}
        setFieldSize={(value) => updateConfig({ fieldSize: value })}
        initialValue={localConfig.uiMeta.initialSliderValue?.toString() || ''}
        setInitialValue={(value) => updateConfig({ initialSliderValue: value ? parseFloat(value) : undefined })}
        minValue={localConfig.uiMeta.minValueSlider?.toString() || ''}
        setMinValue={(value) => updateConfig({ minValueSlider: value ? parseFloat(value) : undefined })}
        maxValue={localConfig.uiMeta.maxValueSlider?.toString() || ''}
        setMaxValue={(value) => updateConfig({ maxValueSlider: value ? parseFloat(value) : undefined })}
        unit={localConfig.uiMeta.unit || ''}
        setUnit={(value) => updateConfig({ unit: value })}
        stepValue={localConfig.uiMeta.stepValue?.toString() || '1'}
        setStepValue={(value) => updateConfig({ stepValue: value ? parseFloat(value) : undefined })}
        showStepValues={localConfig.uiMeta.showStepValues || false}
        setShowStepValues={(value) => updateConfig({ showStepValues: value })}
        showStepPointers={localConfig.uiMeta.showStepPointers || false}
        setShowStepPointers={(value) => updateConfig({ showStepPointers: value })}
        showMinMaxLabel={localConfig.uiMeta.showMinMaxLabel ?? true}
        setShowMinMaxLabel={(value) => updateConfig({ showMinMaxLabel: value })}
      />
    ),
    // showDescriptionSettings(localFieldDataType) && (
    //   <DescriptionSettings
    //     key="description"
    //     content={localConfig.uiMeta.descriptionContent || ''}
    //     setContent={(value) => updateConfig({ descriptionContent: value })}
    //   />
    // ),
    // showAudioVideoEmbedSettings(localFieldDataType) && (
    //   <AudioVideoEmbedSettings
    //     key="audio-video-embed"
    //     audioVideoFiles={localConfig.uiMeta.audioVideoFiles || []}
    //     setAudioVideoFiles={(value) => updateConfig({ audioVideoFiles: value })}
    //     onFilesChange={(value) => updateConfig({ audioVideoFiles: value })}
    //   />
    // ),
    showMandatoryFieldSettings(localFieldDataType) && (
      <MandatoryFieldSettings
        key="mandatory"
        isRequired={localConfig.uiMeta.isRequired || false}
        setIsRequired={(value) => updateConfig({ isRequired: value })}
      />
    ),
    // showVisibilitySettings(localFieldDataType) && (
    //   <VisibilitySettings
    //     key="visibility"
    //     visibilityStatus={
    //       localConfig.uiMeta.hideField ? 'hidden' :
    //         localConfig.uiMeta.disabled ? 'disabled' : 'visible'
    //     }
    //     setVisibilityStatus={(value) => updateConfig({
    //       hideField: value === 'hidden',
    //       disabled: value === 'disabled'
    //     })}
    //   />
    // )
  ].filter(Boolean);
  const onClose = () => {
    setSelectedFieldId(null);
    setLocalFieldName('');
    setLocalFieldScope('');
    // setLocalIsGlobalScopeField(false);
    setLocalConfig(config);
    setFieldTypeError(null);
    setFieldNameError(null);
    setFieldScopeError(null);
    setLocalFieldDataType(undefined);
    setLocalFieldAccessKey(null);
    setLocalAccessKeySuffix('');
    setFieldAccessKeyError(null);
    setIsAccessKeyAvailable(null);
    onHide();
  };
  return (
    <Modal show={show} onHide={onHide} className='border-0'>
      <ModalOverlay>
        <ModalContent theme={theme}>
          <FormTitle title={readOnly ? 'View Field' : isCreating ? 'Create Custom Field' : 'Edit Field Properties'} />
          <div style={{ position: 'relative' }}>
            <FormContainer theme={theme}>
              <div className="px-3 py-4 d-flex flex-column" style={{ backgroundColor: '#0556CB0D' }}>
                {isCreating &&
                  <div className="mb-2 p-4" style={{ backgroundColor: theme.CorporateTheme.colors.primaryLightBG, borderRadius: '8px', border: '1px dashed #BABDC1' }}>
                    <StyledText className="mb-2 text-center" fontSize="16px" fontWeight={600}>
                      Start Creating New Custom Field
                    </StyledText>
                    <StyledText className="text-center mb-3" color='#585558' fontSize="14px" fontWeight={500}>
                      Select field type and enter field name to get started.
                    </StyledText>

                    <AMSSelect
                      showIcons
                      endpoint="/fields/data-types"
                      placeholder="Select field data type here"
                      value={selectedDataTypeOption ?? null}
                      onChange={handleDataTypeSelect}
                      mapResponse={mapDataTypesResponse}
                      error={fieldTypeError ?? undefined}
                      defaultOptions
                    />
                  </div>
                }
                {isCreating ? (
                  <div className="mb-4 mt-0 w-100">
                    <FieldLabel theme={theme}>
                      Field Scope <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                    </FieldLabel>
                    <AMSSelect
                      endpoint="/scopes"
                      placeholder="Select field scope"
                      value={selectedScopeOption ?? null}
                      onChange={handleScopeSelect}
                      mapResponse={mapScopesResponse}
                      additionalParams={scopesAdditionalParams}
                      error={fieldScopeError ?? undefined}
                      isSearchable={false}
                      defaultOptions
                    />
                  </div>
                ) : (
                  <div className="mb-4 w-100">
                    <FieldLabel theme={theme}>Field Scope</FieldLabel>
                    <StyledText
                      fontSize="14px"
                      fontWeight={500}
                      color={theme.CorporateTheme.colors.textPrimary}
                    >
                      {selectedScopeLabel}
                    </StyledText>
                  </div>
                )}
                <div className="mb-4 w-100">
                  <FieldLabel theme={theme}>
                    Field Name <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                  </FieldLabel>
                  <CustomInput
                    value={localFieldName ?? ''}
                    onChange={(val) => {
                      setLocalFieldName(val);
                      setFieldNameError(null); // Clear error when typing
                    }}
                    placeholder="Enter field name here"
                    width="100%"
                    required
                    error={fieldNameError ?? undefined} // <-- Pass error to CustomInput
                  />
                </div>
                {/* Access Key - editable for create, read-only for edit */}
                {isCreating ? (
                  <div className="mb-4 w-100">
                    <FieldLabel theme={theme}>
                      Field Access Key <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                    </FieldLabel>
                    <CustomInput
                      value={localAccessKeySuffix}
                      onChange={(val) => {
                        setLocalAccessKeySuffix(val);
                        setFieldAccessKeyError(null);
                        setIsAccessKeyAvailable(null);
                      }}
                      onBlur={async () => {
                        if (!localAccessKeySuffix || !localAccessKeySuffix.trim()) {
                          setFieldAccessKeyError('Field access key suffix is required');
                          setIsAccessKeyAvailable(false);
                        } else {
                          await checkAccessKeyAvailability(fullAccessKey);
                        }
                      }}
                      placeholder="Enter access key suffix"
                      width="100%"
                      error={fieldAccessKeyError ?? undefined}
                    />
                    {fullAccessKey && localAccessKeySuffix && (
                      <StyledText
                        fontSize="12px"
                        fontWeight={400}
                        color={theme.CorporateTheme.colors.textSecondary}
                        style={{ marginTop: '4px' }}
                      >
                        Access key will be: {fullAccessKey}
                      </StyledText>
                    )}
                  </div>
                ) : (
                  <div className="mb-4 w-100">
                    <FieldLabel theme={theme}>
                      Field Access Key
                    </FieldLabel>
                    <StyledText
                      fontSize="14px"
                      fontWeight={500}
                      color={theme.CorporateTheme.colors.textPrimary}
                    >
                      {localFieldAccessKey || "N/A"}
                    </StyledText>
                  </div>
                )}

                {!isCreating && <div className="mb-4 w-100">
                  <FieldLabel theme={theme}>
                    Field Data Type
                  </FieldLabel>
                  <StyledText
                    fontSize="14px"
                    fontWeight={500}
                    color={theme.CorporateTheme.colors.textPrimary}
                  >
                    {localFieldDataType ? localFieldDataType.replace(/_/g, " ") : "N/A"}
                  </StyledText>
                </div>}
                {localFieldDataType === FieldDataTypeEnum.DATE_TIME && (
                  <div className="mb-0 w-100">
                    <CheckboxItem theme={theme} >
                      <input
                        type="checkbox"
                        checked={Boolean(localConfig?.uiMeta?.includeTimeInDate)}
                        onChange={(e) => {
                          updateConfig({ includeTimeInDate: e.target.checked });
                        }} />
                      <FieldLabel theme={theme}>
                        Include time (for Date-Time field)
                      </FieldLabel>
                    </CheckboxItem>
                  </div>
                )}

              </div>

              {renderableSections.map((Component, index) => (
                <SectionWrapper theme={theme} key={index} alternate={renderableSections.length > 2 && index % 2 === 1}>
                  {Component}
                </SectionWrapper>
              ))}
            </FormContainer>
            {readOnly && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'transparent',
                zIndex: 20,
                pointerEvents: 'all'
              }} />
            )}
          </div>
          <FormActions>
            <Button theme={theme} type="button" $variant="cancel" onClick={onClose}>
              <CloseIcon color={theme.CorporateTheme.colors.textSecondary} /> Cancel
            </Button>
            {!readOnly && (
              <Button
                theme={theme}
                type="submit"
                $variant="add"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <span className="d-flex align-items-center">
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Saving...
                  </span>
                ) : (
                  <>
                    <SaveIcon color={theme.CorporateTheme.colors.background} /> Save
                  </>
                )}
              </Button>
            )}
          </FormActions>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

// Styled Components
export const FieldLabel = styled(StyledText)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: theme?.CorporateTheme?.colors?.headerColor,
  margin: '10px 0',
}));

const SectionWrapper = styled('div')<{ alternate: boolean }>(
  ({ theme, alternate }) => ({
    // paddingBottom: '16px',
    ...(alternate && {
      backgroundColor: theme.CorporateTheme.colors.headerBG,
    }),
  })
);
export const ErrorText = styled(StyledText)(({ theme }) => ({
  fontSize: '11px',
  color: theme.CorporateTheme.colors.danger,
  marginTop: '4px',
  fontWeight: 500,
}));